import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  useCustomer,
  resetIdentityPreservingOnlineJourney,
} from '../customer-context.js';
import { useAgentTeller } from '../agent-teller.js';
import { ArdowenLogo } from '../components/ArdowenLogo.js';
import { DobPicker } from '../components/DobPicker.js';
import { mediumSecurityRouteFor } from '../routing/security-routing.js';
import { onlineCardDisplay } from '../lib/online-card.js';

/**
 * SCRN-02 — Customer Search (CCF-44).
 *
 * Flexible customer lookup for unauthenticated inbound and outbound calls,
 * where the customer has not authenticated or does not have their Open24 /
 * account / card number to hand. Faithful port of the customer's Figma
 * "Customer search" design: four search criteria, a result LIST
 * (Name / Address / DoB) with Next/Previous paging (max 20 results), a
 * constantly-displayed ID&V link, and dummy-card handling for customers with
 * no online card on file.
 *
 * Calls the Teller-ID-gated BFF (`POST /api/customers/search`) through the
 * generated, contract-typed client (`web/src/api`); the request body and the
 * `{ Results, Total }` response are inferred from `app/openapi.yaml`.
 *
 * Available to all roles (CCF-44). The banner is deliberately NOT populated
 * here — that happens at the Medium Security step once the agent selects a
 * customer; this screen only lists candidates.
 */

/** The four CCF-44 search modes. */
type SearchMode = 'card' | 'account' | 'name-dob' | 'name-address';

/** A single result row, as returned by `customer/search`. */
interface SearchHit {
  CustomerNumber: string;
  CustomerName: string;
  /** Customer title (Mr./Mrs./Miss/Dr.) when provided by the gateway — shown in a separate column before the name. */
  CustomerTitle?: string;
  Address: string;
  Dob: string;
  Segment?: string;
  OnlineCardNumber: string;
  HasOnlineCard: boolean;
  /**
   * Online card registration status (CCF-44): registered / unregistered /
   * deregistered. Optional — when the gateway does not supply it we derive a
   * display label from `HasOnlineCard` (an online card on file ⇒ "Registered").
   */
  CardStatus?: string;
  /**
   * #851 CR Change 1 (CCRS-3943): the gateway flags the customer as holding
   * more than one online card.
   */
  MultipleOnlineCards?: boolean;
  /**
   * #851: set by the BFF only for a flagged customer on a name search or a
   * sort code + account (product) search. `loaded` ⇒ {@link OnlineCards} holds
   * one entry per online card (product search: per MATCHING card);
   * `unavailable` ⇒ a card lookup failed (rare), so the customer is shown
   * exactly as before this change — a single row under the online card the
   * search returned — and stays selectable (@dylan219, #851); except a product
   * search with `ProductSearchMatch: 'no-match'`, which is not selectable.
   */
  OnlineCardsStatus?: 'loaded' | 'unavailable';
  OnlineCards?: SearchOnlineCard[];
  /**
   * #851 CR Change 2 (CCRS-3943): set by the BFF only for a flagged customer on
   * a product search, or on a 16-digit debit card search (#851, @dylan219
   * 2026-10-15 — the searched account is the one the debit card is on, matched
   * server-side on the full card number). `no-match` (§5.4) is not selectable;
   * `unverified` (§5.5) falls back to today's row.
   */
  ProductSearchMatch?: 'matched' | 'no-match' | 'unverified';
  /**
   * Client-only: the online card the agent selected on a multi-card row. Sent
   * to the BFF as `selectedOnlineCardNumber`, which binds the session card only
   * to a card it enumerated itself for the CIF.
   */
  SelectedOnlineCardNumber?: string;
}

/** #851: one online card of a customer holding several (BFF-enumerated). */
interface SearchOnlineCard {
  OnlineCardNumber: string;
  CardType: 'ONLI' | 'BONL';
  Last4: string;
  CardStatus: string;
  AccountsStatus: 'loaded' | 'unavailable';
  Accounts: { SortCode: string; AccountNumber: string }[];
}

/**
 * #851 CR Change 1 / Figure 2: the "Online card number" cell shows the FULL
 * online card number as `card/list` returned it (@dylan219, #851: "Show the
 * full card number"), with the card type underneath so a personal and a
 * business online card under one CIF can be told apart.
 */
function onlineCardTypeLabel(card: SearchOnlineCard): string {
  return card.CardType === 'BONL'
    ? 'Business online card'
    : 'Personal online card';
}

/** #851: Card status label for one online card row (Figure 2). */
function onlineCardStatusLabel(card: SearchOnlineCard): string {
  const s = (card.CardStatus ?? '').trim().toUpperCase();
  if (!s || s === 'ACTIVE' || s === 'ACTIVATED') return 'Registered';
  return s.charAt(0) + s.slice(1).toLowerCase();
}

/**
 * #851: the hit to select for one online card row — the customer with the
 * SELECTED card as its online card (CR §4.4). The BFF re-validates the card
 * against its own enumeration before binding it.
 */
function selectionFor(hit: SearchHit, card: SearchOnlineCard): SearchHit {
  return {
    ...hit,
    OnlineCardNumber: card.OnlineCardNumber,
    HasOnlineCard: true,
    SelectedOnlineCardNumber: card.OnlineCardNumber,
  };
}

/**
 * #851 CR Change 2 §5.4: the inline indicator for a product-search customer
 * none of whose online cards reaches the searched account (a confirmed
 * absence — fail closed, Q4). A failed card lookup (§4.5 / §5.5) is NOT
 * blocked: it falls back to today's row (@dylan219).
 */
const NO_MATCHING_CARD_MESSAGE =
  'No online card gives access to this account. This customer cannot be selected under an online card.';

/**
 * #851: true when a flagged customer must not be selected — only when no
 * online card reaches the searched account on a product search (§5.4, Q4).
 */
function isCardSelectionBlocked(hit: SearchHit): boolean {
  return hit.ProductSearchMatch === 'no-match';
}

/** #851 CR Change 2: Card status for a product-search row that is blocked. */
function blockedCardStatusLabel(hit: SearchHit): string | undefined {
  return isCardSelectionBlocked(hit) ? 'No matching online card' : undefined;
}

/**
 * #851 (@dylan219): a flagged customer whose card lookup failed is selected as
 * before this change — under the online card the search returned. That card
 * is sent as the selection; the BFF accepts it only because it is the search
 * row's own card (re-derived server-side).
 */
function withFallbackSelection(hit: SearchHit): SearchHit {
  return hit.OnlineCardsStatus === 'unavailable' &&
    !isCardSelectionBlocked(hit) &&
    hit.OnlineCardNumber
    ? { ...hit, SelectedOnlineCardNumber: hit.OnlineCardNumber }
    : hit;
}

/** #851: the body field carrying the selected online card, when there is one. */
function selectedCardBody(hit: SearchHit): Record<string, string> {
  return hit.SelectedOnlineCardNumber
    ? { selectedOnlineCardNumber: hit.SelectedOnlineCardNumber }
    : {};
}

/**
 * CCF-44 online-card status label shown in the results list. The gateway may
 * carry an explicit `CardStatus` (registered / unregistered / deregistered); if
 * it does not, fall back to whether a real online card is on file.
 */
function cardStatusLabel(hit: SearchHit): string {
  if (hit.CardStatus && hit.CardStatus.trim()) {
    const s = hit.CardStatus.trim().toLowerCase();
    if (s.startsWith('reg')) return 'Registered';
    if (s.startsWith('dereg')) return 'Deregistered';
    if (s.startsWith('unreg')) return 'Unregistered';
    return hit.CardStatus;
  }
  return hit.HasOnlineCard ? 'Registered' : 'No online card';
}

/** CCF-44: a results page holds at most this many rows. */
const PAGE_SIZE = 6;

/**
 * CCF-44 card-number lengths accepted by the single card field: an Online card
 * number (Open24/Business24/Broker) is 10 digits, a Debit/Credit card number is
 * 16 digits (issue #65). Any other length is warned against before searching.
 */
const ONLINE_CARD_NUMBER_LENGTH = 10;
const DEBIT_CREDIT_CARD_NUMBER_LENGTH = 16;

/**
 * Validates the card-number search field (issue #65). The field accepts either
 * an Online card number (10 digits) OR a Debit/Credit card number (16 digits).
 * Returns an inline warning message, or `null` when the format is valid. An
 * empty value returns `null` — the "complete the fields" prompt covers that.
 */
function cardNumberFormatError(value: string): string | null {
  if (!value) return null;
  if (!/^[0-9]+$/.test(value)) {
    return 'Please enter numbers only';
  }
  if (
    value.length !== ONLINE_CARD_NUMBER_LENGTH &&
    value.length !== DEBIT_CREDIT_CARD_NUMBER_LENGTH
  ) {
    return `Card number must be ${ONLINE_CARD_NUMBER_LENGTH} digits (online card) or ${DEBIT_CREDIT_CARD_NUMBER_LENGTH} digits (debit/credit card)`;
  }
  return null;
}

/**
 * CCF-44 name lengths. Two uses share these numbers:
 *  - the gateway keys the search on the first 6 alphabetic characters of the
 *    surname / first 3 of the first name (`customer/search` — see
 *    `customer.r4.yaml`); punctuation is NOT part of that WIRE key, so
 *    {@link toGatewayName} strips it before truncating; and
 *  - the visible input is capped at 6/3 TOTAL characters and, per @dylan219's
 *    #615 ruling (2026-08-11 — "honour the gateway and allow A-z only"), now
 *    accepts A–Z letters only, so the visible value equals the gateway key
 *    form. See {@link filterNameForDisplay}.
 */
const SURNAME_GATEWAY_LENGTH = 6;
const FIRST_NAME_GATEWAY_LENGTH = 3;

/**
 * Display filter for the name fields (issue #615 / CCF-44). The PTSB gateway
 * keys `customer/search` on ASCII A–Z letters ONLY — it rejects apostrophes,
 * hyphens, spaces and accented letters with `Error.Code 1430 "Invalid
 * Characters Detected"` (see {@link toGatewayName}). Per @dylan219's ruling on
 * 2026-08-11 — "Yes lets honour the gateway and allow A-z only please" — the
 * visible field now accepts A–Z letters only: accented letters are decomposed
 * to their base letter (NFD) and everything that is not `[a-zA-Z]` is stripped,
 * so the field value matches the gateway search-key form exactly. So "O'Brien"
 * shows as "OBrien" and "Mac Cárthaigh" shows as "MacCarthaigh" (before the
 * cap).
 *
 * This is a DELIBERATE, authorised reversal of the earlier #423/#469/#513
 * display-parity behaviour, which kept apostrophes/hyphens/spaces/fadás in the
 * visible value so the agent could type real Irish names. Dylan's #615 ruling
 * supersedes it: the display now equals the gateway form, so what the agent sees
 * is exactly what is searched.
 *
 * The {@link SURNAME_GATEWAY_LENGTH} (6) / {@link FIRST_NAME_GATEWAY_LENGTH} (3)
 * TOTAL cap is unchanged; since only letters remain, the cap now counts letters
 * only. "O'Brien" → "OBrien" (6, at the cap); "Mac Cárthaigh" → "MacCar".
 */
const DISPLAY_NAME_MAX = 40;
function filterNameForDisplay(
  value: string,
  maxTotal = DISPLAY_NAME_MAX,
): string {
  // #615 (@dylan219): honour the gateway — decompose accents (NFD) and keep
  // ASCII A–Z letters ONLY, so the visible value equals the gateway search key
  // (see toGatewayName). Then cap the TOTAL character count.
  const filtered = value.normalize('NFD').replace(/[^a-zA-Z]/g, '');
  return [...filtered].slice(0, maxTotal).join('');
}

/**
 * Sanitise a name for the gateway search key (issue #423 / CCF-44): strip every
 * non-alphabetic character (apostrophes, hyphens, spaces, digits, symbols, and
 * accents via NFD decomposition) and truncate to the gateway key length. This
 * MUST run before the surname/first-name reach `customer/search`: the real PTSB
 * gateway rejects any apostrophe (or other punctuation) in these fields with
 * `Error.Code 1430 "Validation of Request has failed - Invalid Characters
 * Detected"`, which fails the whole lookup. It also matches on the alphabetic-
 * only stored form, so "O'Brien" is found by sending "OBRIE". As of #615 the
 * visible field is also A–Z-only ({@link filterNameForDisplay}), so the display
 * value already equals this gateway form — this remains the authoritative wire
 * sanitiser regardless.
 */
function toGatewayName(value: string, maxLength: number): string {
  return value
    .normalize('NFD')
    .replace(/[^a-zA-Z]/g, '')
    .slice(0, maxLength);
}

/** Formats an ISO date (YYYY-MM-DD) as DD/MM/YYYY for display. */
function formatDob(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Converts a DD/MM/YYYY input to the API's YYYY-MM-DD, or '' if incomplete. */
function toIsoDob(ddmmyyyy: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(ddmmyyyy.trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

const MODES: { value: SearchMode; label: string }[] = [
  { value: 'card', label: 'Card number' },
  { value: 'account', label: 'Sort code & account number' },
  { value: 'name-dob', label: 'Name & date of birth' },
  { value: 'name-address', label: 'Name & address' },
];

/**
 * Issue #72 (@dylan219, 2026-08-21) — on an ONLINE journey the ONLY roles
 * permitted to skip ID&V from the Customer Search screen (identified → Medium
 * Security) are ECL Agent, Supervisor, Floor Walker (FloorSupport) and Admin. Any
 * OTHER role that selects a REGISTERED customer while on an ONLINE interaction is
 * shown a verification-required warning with a "Go to ID&V" button instead of
 * skipping; ID&V is then driven from the online card number returned in the
 * search (card-number lookup → PAN option, the normal ID&V process).
 *
 * @dylan219 (2026-08-21, follow-up): "These changes should only apply to Online
 * Journeys." This role restriction + warning therefore apply to ONLINE journeys
 * ONLY. On an OFFLINE journey there is no live caller to verify, so ALL roles
 * skip straight to the dashboard — this set is NOT consulted offline. The role
 * identifiers match the RBAC role set (`server/src/rbac/data`).
 */
const SKIP_IDV_ROLES: ReadonlySet<string> = new Set([
  'ECLAgent',
  'Supervisor',
  'FloorSupport',
  'Admin',
]);

const labelClass =
  "text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]";
const inputClass =
  "px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f]";

const cellClass =
  "px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c] align-top";
const selectButtonClass =
  "px-4 py-2 bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] font-bold text-[14px] rounded-md hover:bg-[#e55a2a] transition-all focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

/**
 * #851 CR Change 1 (CCRS-3943, Figure 2): a customer holding several online
 * cards is shown as one row per online card. The customer details (Title /
 * Name / Address / Date of birth) are merged across the customer's rows and the
 * rows are visually grouped (shared background + left accent) so the agent can
 * see they belong to one CIF. Each row carries its own Online card number,
 * Accounts (sort code / account number — Q5), Card status and Select.
 */
function MultiCardRows({
  hit,
  cards,
  disabled,
  onSelectCard,
}: {
  hit: SearchHit;
  cards: SearchOnlineCard[];
  disabled: boolean;
  onSelectCard: (card: SearchOnlineCard) => void;
}): React.JSX.Element {
  const span = cards.length;
  return (
    <>
      {cards.map((card, i) => (
        <tr
          key={`${hit.CustomerNumber}-${card.OnlineCardNumber}`}
          data-customer-group={hit.CustomerNumber}
          className={`bg-[#f9fafb] ${
            i === span - 1
              ? 'border-b-2 border-[#dad7d3]'
              : 'border-b border-dashed border-[#e5e7eb]'
          }`}
          aria-label={`Customer ${hit.CustomerName}, online card ending ${card.Last4}`}
        >
          {i === 0 && (
            <>
              <td
                rowSpan={span}
                className={`${cellClass} border-l-4 border-[#fc4c02]`}
              >
                {hit.CustomerTitle || '—'}
              </td>
              <td rowSpan={span} className={cellClass}>
                {/* The name is its own element (not a bare text node beside the
                    card-count caption) so it reads — and is located — as one
                    exact "customer name" across the merged card rows. */}
                <span className="block">{hit.CustomerName}</span>
                <span className="block text-[12px] text-[#6b7280]">
                  {hit.ProductSearchMatch === 'matched'
                    ? // CR Change 2 §5.3: only the card(s) reaching the
                      // searched account are listed.
                      `${span} matching online ${span === 1 ? 'card' : 'cards'}`
                    : `${span} online cards`}
                </span>
              </td>
              <td rowSpan={span} className={cellClass}>
                {hit.Address || '—'}
              </td>
              <td rowSpan={span} className={cellClass}>
                {hit.Dob ? formatDob(hit.Dob) : '—'}
              </td>
            </>
          )}
          <td className={cellClass}>
            <span className="whitespace-nowrap">{card.OnlineCardNumber}</span>
            <span className="block text-[12px] text-[#6b7280]">
              {onlineCardTypeLabel(card)}
            </span>
          </td>
          <td className={cellClass}>
            {card.AccountsStatus === 'unavailable' ? (
              // CR §4.5: the card was enumerated but its accounts could not be
              // read — mark the column unavailable; the card stays selectable.
              <span className="text-[#b91c1c]">Accounts unavailable</span>
            ) : card.Accounts.length === 0 ? (
              '—'
            ) : (
              <ul className="m-0 p-0 list-none">
                {card.Accounts.map((a) => (
                  <li
                    key={`${a.SortCode}/${a.AccountNumber}`}
                    className="whitespace-nowrap"
                  >
                    {a.SortCode} / {a.AccountNumber}
                  </li>
                ))}
              </ul>
            )}
          </td>
          <td className={cellClass}>{onlineCardStatusLabel(card)}</td>
          <td className="px-4 py-3 align-top">
            <button
              type="button"
              onClick={() => onSelectCard(card)}
              disabled={disabled}
              aria-label={`Select ${hit.CustomerName} online card ending ${card.Last4}`}
              className={selectButtonClass}
            >
              Select
            </button>
          </td>
        </tr>
      ))}
    </>
  );
}

export function CustomerSearchScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { customer, setCustomer } = useCustomer();

  // Teller ID (issue #4 / #24): sourced solely from the agent's user record
  // (session). It is sent automatically on every Teller-ID-gated call and is
  // no longer shown or entered in the UI. When the agent has no assigned Teller
  // ID, `tellerId` is empty and the search is refused with a clear
  // "No teller ID found" message rather than the generic permission error.
  const agentTellerId = useAgentTeller();
  const tellerFromProfile = /^\d{4}$/.test(agentTellerId ?? '');
  const tellerId = tellerFromProfile ? agentTellerId! : '';
  const [mode, setMode] = useState<SearchMode>('card');

  // Per-mode inputs.
  const [cardNumber, setCardNumber] = useState('');
  // Inline card-number length warning (issue #65): 10 or 16 digits required.
  const [cardNumberError, setCardNumberError] = useState('');
  const [nsc, setNsc] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [surname, setSurname] = useState('');
  const [firstName, setFirstName] = useState('');
  const [dob, setDob] = useState('');
  const [address, setAddress] = useState('');

  const [results, setResults] = useState<SearchHit[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [page, setPage] = useState(0);
  const [message, setMessage] = useState('');
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  // The search criteria that produced the current results, retained so the
  // offline-identify call (issue #72) can re-run the SAME search server-side to
  // re-confirm the selected customer from trusted gateway data.
  const [lastSearchBody, setLastSearchBody] = useState<Record<
    string,
    string
  > | null>(null);

  // Issue #72/#68 — flag-gated OFFLINE-journey ID&V relaxation. Learned from
  // `/api/session`: `offlineJourneySkipIdv` (default false ⇒ existing ID&V
  // routing, unchanged) and the live Online/Offline interaction `state` (the
  // app already resolves it). Online detection is tracked separately (#74); here
  // we use whatever state the session reports (offline absent a live call).
  const [skipIdv, setSkipIdv] = useState(false);
  const [interactionState, setInteractionState] = useState<
    'online' | 'offline'
  >('offline');
  // Issue #72 (@dylan219) — the agent's RBAC role, read from `/api/session`.
  // Only the four privileged roles (see SKIP_IDV_ROLES) may skip ID&V from the
  // Customer Search screen; every other role selecting a REGISTERED customer on
  // an ONLINE interaction is sent through ID&V via the verification-required
  // warning below. Empty until the session resolves ⇒ treated as NON-privileged
  // (fail-safe: never skip ID&V for an unknown role).
  const [role, setRole] = useState('');
  // Tracks whether `/api/session` has resolved. Until it has, the
  // `skipIdv` / `interactionState` values are still at their defaults, so
  // selecting a customer would silently take the OLD ID&V routing regardless of
  // the (not-yet-known) flag. We therefore gate customer selection on this so
  // the routing decision is never made against an unresolved session.
  const [sessionLoaded, setSessionLoaded] = useState(false);
  // #357: the Online/Offline journey is now established ONCE at app entry into
  // the BFF session (App.tsx → POST /genesys/session-context), so this screen
  // forwards NOTHING to derive it — the server resolves `state` from the
  // session. This also fixes #342: the screen used to forward the client-side
  // `customer.contextConversationId` as `X-Genesys-Interaction-Id`, and a STALE
  // value left by a PRIOR online call made a genuinely OFFLINE search resolve as
  // an unverified claim → wrongly routed to Medium Security. The BFF's
  // session-first resolution (+ the #270 participant re-validation) is the
  // authority now. Deliberately mount-only (empty deps).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/session');
        if (cancelled || !data) return;
        setSkipIdv(Boolean(data.offlineJourneySkipIdv));
        if (data.state === 'online' || data.state === 'offline') {
          setInteractionState(data.state);
        }
        // Issue #72 — capture the agent's role so the skip-ID&V decision below can
        // gate on the four privileged roles.
        if (typeof data.role === 'string') setRole(data.role);
      } catch {
        // Fail-safe: on any error keep the defaults (flag OFF ⇒ existing ID&V
        // routing), so a session-fetch hiccup never relaxes verification.
      } finally {
        // Mark the session resolved either way: on success the flag/state are
        // now known; on failure we deliberately fall back to the safe defaults
        // (flag OFF ⇒ existing ID&V routing) — both are a settled decision, so
        // selection can proceed.
        if (!cancelled) setSessionLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the #270 note above.
  }, []);
  // CCF-44 "Error 2" pop-up: shown when a selected customer HAS an Open24
  // number on Unibanks — they must go through standard ID&V or re-register.
  const [error2For, setError2For] = useState<SearchHit | null>(null);
  // Issue #72 (@dylan219) — verification-required warning pop-up: shown when a
  // NON-privileged role selects a REGISTERED customer while on an ONLINE
  // interaction. Its "Go to ID&V" button seeds the online card number from the
  // search into context and navigates to ID&V, where the card-lookup auto-runs
  // and the agent is presented with the normal PAN option.
  const [idvRequiredFor, setIdvRequiredFor] = useState<SearchHit | null>(null);

  const pageCount = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageRows = results.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE,
  );

  /** Builds the request body for the active search mode, or null if incomplete. */
  function buildBody(): Record<string, string> | null {
    switch (mode) {
      case 'card':
        return cardNumber ? { cardNumber } : null;
      case 'account':
        return accountNumber ? { nsc, accountNumber } : null;
      case 'name-dob': {
        const iso = toIsoDob(dob);
        // Sanitise the names for the gateway search key (issue #423): the agent
        // may have typed apostrophes/hyphens/spaces (e.g. O'Brien) for display
        // parity, but the gateway rejects any punctuation (1430) and keys on the
        // alphabetic-only, truncated form.
        const gwSurname = toGatewayName(surname, SURNAME_GATEWAY_LENGTH);
        const gwFirstName = toGatewayName(firstName, FIRST_NAME_GATEWAY_LENGTH);
        return gwSurname && gwFirstName && iso
          ? { surname: gwSurname, firstName: gwFirstName, dob: iso }
          : null;
      }
      case 'name-address': {
        const gwSurname = toGatewayName(surname, SURNAME_GATEWAY_LENGTH);
        const gwFirstName = toGatewayName(firstName, FIRST_NAME_GATEWAY_LENGTH);
        return gwSurname && gwFirstName && address
          ? { surname: gwSurname, firstName: gwFirstName, address }
          : null;
      }
      default:
        return null;
    }
  }

  async function onSearch(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    // The Teller ID comes from the agent's profile and is mandatory on every
    // gated call (enforced server-side). If the agent has no assigned Teller
    // ID, surface a clear message rather than calling the gateway with none
    // (issue #24).
    if (!tellerFromProfile) {
      setMessage('No teller ID found');
      return;
    }
    const body = buildBody();
    if (!body) {
      setMessage('Please complete the fields for the selected search option.');
      return;
    }
    // Issue #65: gate a card-number search on the 10-or-16-digit format so the
    // agent gets a clear warning rather than an unproductive gateway round-trip.
    if (mode === 'card') {
      const cardError = cardNumberFormatError(cardNumber);
      if (cardError) {
        setCardNumberError(cardError);
        setMessage('');
        return;
      }
    }
    setSearching(true);
    setSearched(true);
    setMessage('Searching…');
    setResults([]);
    setPage(0);
    // Retain the criteria so the offline-identify call (issue #72) can re-run the
    // SAME search server-side and re-confirm the selected customer.
    setLastSearchBody(body);
    try {
      const { data, error, response } = await api.POST('/customers/search', {
        // Mandatory 4-digit Teller ID — enforced by the BFF on every call.
        params: { header: { 'X-Teller-Id': tellerId } },
        body,
      });
      if (error || !data) {
        setMessage(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : `Search failed (${response.status}).`,
        );
        return;
      }
      const rows = (data.Results ?? []) as unknown as SearchHit[];
      // Issue #10: a card-number or sort-code + account-number search is a
      // PRECISE-IDENTIFIER lookup — a single match is unambiguous, so proceed
      // straight to that customer rather than make the agent pick the one row
      // from a list. Name-based searches (which fuzzy-match similar people, and
      // per CCF-44 may legitimately return one of several look-alikes) keep the
      // list view regardless of the count. Multi-result identifier searches
      // (joint / business / broker accounts) also keep the list. `onSelect`
      // runs the SAME context-binding + Error-2 / medium-security routing as a
      // manual click — no Teller-ID / verification control is bypassed. The
      // results list is deliberately NOT rendered on this path (no one-row list).
      //
      // #851 CR Change 2 (CCRS-3943): for a customer holding several online
      // cards, the single row auto-proceeds ONLY when exactly one online card
      // reaches the searched account — and then under THAT card (§5.2 step 5),
      // never the identification API's card. Several matching cards (§5.3) or
      // no matching card (§5.4) keep the list so the agent picks the card with
      // the caller, or sees why it is blocked. A failed card lookup (§5.5)
      // proceeds exactly as before this change, under the identification
      // API's card (@dylan219).
      const single = rows.length === 1 ? rows[0] : undefined;
      const singleMatchedCard =
        single?.ProductSearchMatch === 'matched' &&
        single.OnlineCards?.length === 1
          ? single.OnlineCards[0]
          : undefined;
      const flaggedNeedsList =
        !!single &&
        ((single.OnlineCardsStatus === 'loaded' && !singleMatchedCard) ||
          isCardSelectionBlocked(single));
      if (
        (mode === 'card' || mode === 'account') &&
        single &&
        !flaggedNeedsList
      ) {
        setResults([]);
        setTotal(0);
        setTruncated(false);
        setMessage('');
        // Issue #93: thread the FRESHLY-built `body` explicitly. `onSelect`
        // (→ offline-identify) re-runs the SAME search server-side and needs the
        // criteria. We must NOT read `lastSearchBody` here — `setLastSearchBody`
        // above is an async state update that has NOT flushed yet in this tick,
        // so the closure still sees the PREVIOUS render's value (null on the very
        // first search). That made the first offline-identify omit the criteria →
        // BFF 400 (`invalid_search_criteria`) → "Could not identify… retry", with
        // the retry then succeeding (state since flushed). Passing `body` through
        // makes the first search work too.
        onSelect(
          singleMatchedCard
            ? selectionFor(single, singleMatchedCard)
            : withFallbackSelection(single),
          body,
        );
        return;
      }
      setResults(rows);
      setTotal(data.Total ?? rows.length);
      setTruncated(!!data.Truncated);
      setMessage(rows.length ? '' : 'No customers found.');
    } catch {
      setMessage('Request failed.');
    } finally {
      setSearching(false);
    }
  }

  /**
   * Selecting a customer proceeds to the Medium Security screen (CCF-44: on
   * selection the agent is directed to the appropriate medium-security screen).
   * Per CCF-44 the banner is populated only at this point, so we push the
   * selected customer into context here. The dummy online card is carried
   * forward when the customer has none on file; the banner displays "N/A" so
   * the dummy number is never shown.
   *
   * CCF-44 branch: if the customer HAS an Open24 number on Unibanks they must
   * go through standard ID&V or re-register — the system shows the "Error 2"
   * pop-up directing the agent, rather than proceeding via the dummy-number
   * mechanism (which only applies when the customer has NO online number).
   */
  function onSelect(hit: SearchHit, searchBody?: Record<string, string>): void {
    // Defer the routing decision until `/api/session` has resolved. Until then
    // `skipIdv` / `interactionState` are still at their defaults, so selecting
    // would silently take the OLD ID&V routing irrespective of the (unknown)
    // flag (a timing-dependent fail-safe). The Select buttons are disabled while
    // unresolved; this guards the auto-select (single precise-identifier) path
    // too. The session settles in a tick, so a brief message suffices.
    if (!sessionLoaded) {
      setMessage('Loading… please retry in a moment.');
      return;
    }

    // #851 CR Change 1: a customer holding several online cards is selected
    // per card, so a multi-card row needs a chosen card. (When a card lookup
    // failed the row falls back to today's behaviour — the search row's own
    // card — and is selectable; @dylan219.) CR Change 2 §5.4 (Q4): no online
    // card reaches the searched account — never select under the
    // identification API's card.
    if (isCardSelectionBlocked(hit)) {
      setMessage(NO_MATCHING_CARD_MESSAGE);
      return;
    }
    if (hit.OnlineCardsStatus === 'loaded' && !hit.SelectedOnlineCardNumber) {
      setMessage('Select one of the customer’s online cards.');
      return;
    }

    // Populate the banner context for the SELECTED customer up-front, on EVERY
    // branch. The Error 2 branch routes the agent to ID&V, and ID&V (like
    // Medium Security) operates on whoever is in customer context — so the
    // context MUST reflect this `hit` before we navigate, or verification could
    // run against a previously selected / stale CIF (wrong-customer risk).
    if (!setCustomerContext(hit)) {
      setMessage('Could not select this customer. Please retry.');
      return;
    }

    // #477 (CCF-112): record the "Caller Identified" call-history event — the
    // method used to identify the caller — the moment selection succeeds and a
    // CIF is bound. Best-effort/fire-and-forget: it must never block or fail the
    // navigation, so errors are swallowed. Fired only when a CIF is present.
    recordIdentificationEvent(hit);

    // Issue #72 (@dylan219, 2026-08-21) — searching + SKIPPING ID&V from the
    // Customer Search screen is permitted ONLY for four roles: ECL Agent,
    // Supervisor, Floor Walker (FloorSupport) and Admin (see SKIP_IDV_ROLES).
    // For any OTHER role that selects a REGISTERED customer while on an ONLINE
    // interaction, we do NOT skip ID&V: present a verification-required warning
    // with a "Go to ID&V" button. When they proceed, ID&V is driven from the
    // online card number returned in the search (card-number lookup → PAN option,
    // the normal ID&V process). Registered = an Online card on file
    // (`HasOnlineCard`) — an unregistered customer has no online card to seed the
    // lookup with, so it falls through to the existing routing below.
    const roleMaySkipIdv = SKIP_IDV_ROLES.has(role);
    if (!roleMaySkipIdv && interactionState === 'online' && hit.HasOnlineCard) {
      setIdvRequiredFor(hit);
      return;
    }

    // Issue #72/#68 — flag-gated ID&V relaxation (staging only; default OFF ⇒ the
    // existing ID&V routing below is unchanged). A human-requested change
    // (@dylan219): on selecting an identified customer the app must NO LONGER
    // route to ID&V or check for PAN. OFFLINE (no live call) → straight to the
    // dashboard, after a server-side offline-identify that establishes medium
    // verification BOUND to this customer. ONLINE (live call) → straight to
    // Medium Security (no Error 2, no ID&V); the journey state comes from the
    // session.
    //
    // @dylan219 (2026-08-21, follow-up): the four-role restriction applies to
    // ONLINE journeys ONLY. On an ONLINE journey the skip is confined to the four
    // privileged roles (any other role got the "Go to ID&V" warning above). On an
    // OFFLINE journey there is no live caller to verify, so ALL roles skip
    // straight to the dashboard — no role check.
    if (skipIdv) {
      if (interactionState === 'online') {
        if (!roleMaySkipIdv) {
          // Defensive: a non-privileged online role should already have been
          // routed to the ID&V warning above (registered customers) — do NOT
          // skip for them here. Fall through to the standard ID&V routing.
        } else {
          // #650: bind the identified customer + card to the SESSION server-side
          // BEFORE routing to the security screen. The online security screens
          // reached from Customer Search (Business SCRN-05 / Broker SCRN-58, and
          // the EDS/VISA variants) call `/security/questions`, whose additional-
          // question-answer + address enrichment reads the card-gated `customer/
          // info` + `customer/identification` ops and so needs the session card.
          // Without this bind the online Customer-Search path left the session
          // card-less → those reads threw + were swallowed → the additional
          // answers + addresses rendered blank (@dylan219's #650 report). This
          // grants NO verification (server-side `none`), so the Medium Security
          // screen still performs the real ID&V. #592: route a recognised
          // Business24 customer to Business Medium Security (SCRN-05); segment is
          // read from `hit` (context state is stale in this same tick).
          void onlineIdentifyThenSecurity(hit, searchBody);
          return;
        }
      } else {
        // OFFLINE → straight to the dashboard for ANY role.
        void offlineIdentifyThenDashboard(hit, searchBody);
        return;
      }
    }

    if (hit.HasOnlineCard) {
      // Open24 number on file → Error 2 dialog (its action navigates to ID&V).
      setError2For(hit);
      return;
    }
    // Issue #811 — an ONLINE journey for a customer with NO online card (an
    // unregistered customer): card/PAN ID&V is impossible and the #72 four-role
    // skip is not available to non-privileged roles, so without a bind the
    // session never gets an identified CIF and the Medium→High Security flow
    // 403s `customer_not_identified` on `/security/high/begin` +
    // `/security/joint-associations` (the requireIdentified gate). Bind the
    // IDENTIFIED identity (level `none`, NOT verified — Medium Security still
    // performs the real verification) server-side FIRST, then route to Medium
    // Security exactly as before. ANY role may bind the identity of a customer
    // it just legitimately searched for; this is NOT the #72 ID&V skip (the
    // privileged-role skip branch above already handled the flag-ON path). The
    // OFFLINE no-card journey keeps its existing direct routing unchanged.
    if (interactionState === 'online') {
      void searchIdentifyThenSecurity(hit, searchBody);
      return;
    }
    navigate(mediumSecurityRouteFor({ segment: hit.Segment }));
  }

  /**
   * Issue #72 OFFLINE path: ask the BFF to establish the verified (medium)
   * session context bound to the selected customer, then land on the dashboard.
   * The BFF re-confirms the customer from trusted gateway data (re-running the
   * same search) and refuses (404) when the flag is OFF, so this never weakens a
   * deployment where the relaxation was not turned on. On any failure we surface
   * a message and do NOT navigate (fail-closed — no unverified dashboard).
   */
  async function offlineIdentifyThenDashboard(
    hit: SearchHit,
    searchBody?: Record<string, string>,
  ): Promise<void> {
    setMessage('Identifying customer…');
    try {
      // Issue #93: prefer the explicitly-passed `searchBody` (the freshly-built
      // criteria from the current `onSearch` tick) over `lastSearchBody` state.
      // The auto-select (single precise-identifier) path runs in the SAME tick as
      // `setLastSearchBody`, before that async update flushes, so reading the
      // state there yields the STALE previous value (null on the first search) and
      // the criteria-less body was rejected by the BFF (400) — fixed first time,
      // then "worked on retry". The manual list-Select click happens in a later
      // render, where `lastSearchBody` is already populated, so the fallback is
      // safe.
      const criteria = searchBody ?? lastSearchBody ?? {};
      // #357: the journey is resolved by the BFF from the session-established
      // interaction id (App.tsx → /genesys/session-context) + the #270
      // participant re-validation, so this route forwards only the Teller ID.
      // It no longer sends `X-Genesys-Interaction-Id` from client state — that
      // was #342's bug (a stale `contextConversationId` from a prior call made a
      // genuinely OFFLINE search resolve as an unverified claim → Medium
      // Security). The Genesys token is likewise server-held (#343).
      const { data, error, response } = await api.POST(
        '/verification/offline-identify',
        {
          params: {
            header: {
              'X-Teller-Id': tellerId,
            },
          },
          body: {
            ...criteria,
            customerNumber: hit.CustomerNumber,
            ...selectedCardBody(hit),
          },
        },
      );
      // The BFF resolves Online/Offline server-side; on an ONLINE journey it
      // returns the documented {identified:true, verified:false, online:true}.
      // Route that to Medium Security (which performs the real verification)
      // BEFORE the generic !verified failure check — otherwise a stale
      // client-side interaction state could call this route, get the documented
      // online response, and treat it as a generic failure (stranding the
      // journey). A documented online response is NOT an error.
      if (!error && data?.online === true && data.verified === false) {
        setMessage('');
        // #592: Business24 → Business Medium Security (SCRN-05), else generic.
        navigate(mediumSecurityRouteFor({ segment: hit.Segment }));
        return;
      }
      if (error || !data?.verified) {
        // Inline the BFF/gateway error reason + code beside the action
        // (authoritative @dylan219, #80/#82/#94) so the failure is diagnosable
        // and retryable, rather than only the generic "could not identify".
        // The BFF error body is the contract `Error` shape (`{ error, reason }`);
        // some gateway-backed failures also carry a `code`/`message`. Read all,
        // falling back to the HTTP status when nothing structured is present.
        const e = (error ?? {}) as {
          error?: string;
          reason?: string;
          code?: string | number;
          message?: string;
        };
        const detail =
          e.reason ??
          e.message ??
          e.error ??
          `request failed (${response.status})`;
        const code = e.code != null ? String(e.code) : undefined;
        setMessage(
          `Could not identify this customer. Please retry.${
            code ? ` [${code}]` : ''
          } ${detail}`.trim(),
        );
        return;
      }
      // Reflect the medium clearance in the banner context for the dashboard.
      // #801: must also carry `hit.Dob` (as `setCustomerContext` does — the
      // #492 fix). This update re-spreads the STALE pre-selection `customer`
      // closure plus the `hit` fields, so without an explicit `dob` it silently
      // dropped the search DoB on the offline skip-IDV path, and the Medium/
      // High Security screens (which read `customer.dob`) rendered DoB
      // "Not available" for a customer identified by sort-code + account.
      // #801 review: `dob` is set UNCONDITIONALLY from the CURRENT hit — a
      // conditional spread would leave the PREVIOUS customer's DoB surviving
      // in the `...customer` re-spread when the newly selected hit has none
      // (cross-customer PII exposure). When the hit carries no DoB the context
      // must show none, never a stale one.
      setCustomer({
        ...customer,
        name: hit.CustomerName,
        cif: hit.CustomerNumber,
        onlineCardNumber: hit.HasOnlineCard ? hit.OnlineCardNumber : 'N/A',
        segment: hit.Segment,
        dob: hit.Dob?.trim() || undefined,
        tellerId,
        securityLevel: 'medium',
        customerVerification: 'Y',
      });
      setMessage('');
      navigate('/dashboard');
    } catch {
      setMessage('Could not identify this customer. Please retry.');
    }
  }

  /**
   * Issue #650 ONLINE path: ask the BFF to bind the identified customer + card to
   * the session (server-side, at level `none` — identified, NOT verified), THEN
   * route to the appropriate medium-security screen. The bind gives the security
   * screens' `/security/questions` enrichment the session card the card-gated
   * `customer/info` (additional-question answers) + `customer/identification`
   * (signatory/borrower address) reads require, so the Business/Broker security
   * screens show ALL info when reached via Customer Search. The route returns the
   * documented `{identified:true, verified:false, online:true}` on an online
   * journey; we navigate regardless of the bind outcome (fail-open on navigation:
   * a bind failure only degrades to the prior blank-enrichment behaviour, and the
   * Medium Security screen still performs the real ID&V, so the agent is never
   * stranded).
   */
  async function onlineIdentifyThenSecurity(
    hit: SearchHit,
    searchBody?: Record<string, string>,
  ): Promise<void> {
    const route = mediumSecurityRouteFor({ segment: hit.Segment });
    try {
      // Same #93 precedence as the offline path: prefer the freshly-built
      // criteria passed from the current `onSearch` tick over `lastSearchBody`
      // state (which is stale on the same-tick auto-select path).
      const criteria = searchBody ?? lastSearchBody ?? {};
      await api.POST('/verification/offline-identify', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          ...criteria,
          customerNumber: hit.CustomerNumber,
          ...selectedCardBody(hit),
        },
      });
    } catch {
      // Best-effort: a bind failure must never strand the agent — navigation
      // still proceeds and the Medium Security screen performs the real ID&V.
    }
    navigate(route);
  }

  /**
   * Issue #811 ONLINE no-card path: ask the BFF to bind the IDENTIFIED customer
   * (+ #418 dummy card when none is on file) to the session server-side (level
   * `none` — identified, NOT verified), THEN route to the Medium Security screen
   * for the segment. The bind gives the security flow the session CIF that
   * `requireIdentified` checks on `/security/high/begin` +
   * `/security/joint-associations` (and that `raiseVerification` needs to record
   * the real Medium verification at all). The BFF re-confirms the customer by
   * re-running the SAME search (the `searchBody` criteria) server-side before
   * binding — the client CIF alone is never trusted — and grants NO
   * verification: Medium Security still performs the real verification.
   * Navigation is FAIL CLOSED (ai-review HIGH, #811): this is a
   * security-relevant identification bind, so an agent only reaches Medium
   * Security once the BFF has confirmed the bind — a failed/refused bind
   * surfaces an actionable inline error and does NOT navigate (the alternative
   * is silently recreating the unbound-session `customer_not_identified` state
   * downstream). The agent can retry by selecting the customer again.
   */
  async function searchIdentifyThenSecurity(
    hit: SearchHit,
    searchBody?: Record<string, string>,
  ): Promise<void> {
    const route = mediumSecurityRouteFor({ segment: hit.Segment });
    try {
      // Same #93 precedence as the other bind paths: prefer the freshly-built
      // criteria passed from the current `onSearch` tick over `lastSearchBody`
      // state (stale on the same-tick auto-select path).
      const criteria = searchBody ?? lastSearchBody ?? {};
      const { data, error } = await api.POST('/verification/search-identify', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { ...criteria, customerNumber: hit.CustomerNumber },
      });
      // Fail closed: only a confirmed server-side bind proceeds to Medium
      // Security. (An online journey returns
      // {identified:true, verified:false, online:true}.)
      if (error || !data?.identified) {
        const e = (error ?? {}) as { error?: string };
        setMessage(
          `Could not identify this customer. Please retry.${
            e.error ? ` [${e.error}]` : ''
          }`.trim(),
        );
        return;
      }
    } catch {
      setMessage('Could not identify this customer. Please retry.');
      return;
    }
    navigate(route);
  }

  /**
   * Populate the banner context for the selected customer (CCF-44). Returns
   * `true` on success; on failure it clears any stale context (fail-closed so a
   * previously selected customer can't leak into ID&V) and returns `false`.
   */
  /**
   * #477 (CCF-112): fire the best-effort "Caller Identified" call-history event
   * recording HOW the caller was identified. The current search `mode` maps to
   * one of the five exact business methods; the BFF whitelists the string and
   * binds the event to the CIF. Fire-and-forget — never awaited, errors are
   * swallowed, so it cannot block or fail the customer-selection navigation.
   * Only fires when a CIF is present.
   */
  function recordIdentificationEvent(hit: SearchHit): void {
    if (!hit.CustomerNumber) return;
    const methodByMode: Record<
      SearchMode,
      | 'Agent Identification - O24 Number'
      | 'Agent Identification - Account Number/Sort'
      | 'Agent Identification - Name/DOB'
      | 'Agent Identification - Name/Address'
    > = {
      card: 'Agent Identification - O24 Number',
      account: 'Agent Identification - Account Number/Sort',
      'name-dob': 'Agent Identification - Name/DOB',
      'name-address': 'Agent Identification - Name/Address',
    };
    void api
      .POST('/calls/identification-event', {
        // The route is `requireTellerId`-gated (like /calls/security-event), so
        // the mandatory 4-digit Teller ID header must be sent or it 403s and the
        // event never records (increment-1 gap, #477).
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber: hit.CustomerNumber,
          method: methodByMode[mode],
        },
      })
      .catch(() => {
        /* best-effort record write — never block selection */
      });
  }

  function setCustomerContext(hit: SearchHit): boolean {
    try {
      if (!hit.CustomerNumber) throw new Error('missing customer number');
      setCustomer({
        ...customer,
        name: hit.CustomerName,
        cif: hit.CustomerNumber,
        // Real card → display it; no card on file → "N/A" (dummy never shown).
        onlineCardNumber: hit.HasOnlineCard ? hit.OnlineCardNumber : 'N/A',
        segment: hit.Segment,
        // #492: carry the DoB the gateway returned on the search into the banner
        // context so the online Customer-Search → Medium Security path shows it
        // (SCRN-03 reads `customer.dob`). Without this the DoB was dropped here
        // and Medium Security rendered "Not available", even though it displays
        // via ID&V (which sets `dob`) and in this screen's own results list.
        // `hit.Dob` is the ISO `YYYY-MM-DD` the BFF normalises the search
        // response to (mapSearchContent), matching the context contract.
        // #801 review: UNCONDITIONAL so a newly selected hit WITHOUT a DoB
        // clears the field — a conditional spread over the `...customer`
        // re-spread would leave the PREVIOUS customer's DoB surviving into the
        // new selection's context (cross-customer PII exposure).
        dob: hit.Dob?.trim() || undefined,
        tellerId,
        securityLevel: 'none',
      });
      return true;
    } catch {
      // Fail-closed: drop any previously selected customer from context.
      setCustomer({ securityLevel: 'none' });
      return false;
    }
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="search-heading"
    >
      <h1
        id="search-heading"
        className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
      >
        Customer Search
      </h1>
      <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6">
        Search for a customer using any one of the options below.
      </p>

      {/* Constant ID&V link (CCF-44). */}
      <div className="mb-8 bg-[#f3f4f6] border-l-4 border-[#fc4c02] pl-4 py-3 w-[760px] max-w-full">
        <a
          href="/idv"
          onClick={(e) => {
            e.preventDefault();
            navigate('/idv');
          }}
          className="text-[16px] font-['Arial',sans-serif] text-[#fc4c02] underline hover:text-[#b83205] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          Go to ID&amp;V (Customer Verification)
        </a>
      </div>

      <form onSubmit={onSearch} className="max-w-[760px]">
        {/* The mandatory 4-digit Teller ID is sourced from the agent's profile
            and sent automatically on every gated call (issue #4 / #24); it is
            no longer shown or entered here. */}

        {/* Search-mode selector. */}
        <div className="mb-6 flex flex-col gap-2 w-[400px]">
          <label htmlFor="search-mode" className={labelClass}>
            Search by
          </label>
          <select
            id="search-mode"
            value={mode}
            onChange={(e) => {
              setMode(e.target.value as SearchMode);
              setMessage('');
            }}
            className="px-4 py-3 border-2 border-[#dad7d3] bg-white rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] cursor-pointer focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20"
          >
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Mode-specific inputs. */}
        <div className="mb-6 p-8 border-2 border-[#dad7d3] bg-white">
          {mode === 'card' && (
            <div className="flex flex-col gap-2">
              <label htmlFor="card-number" className={labelClass}>
                Card number
              </label>
              <input
                id="card-number"
                value={cardNumber}
                onChange={(e) => {
                  const next = e.target.value.trim();
                  setCardNumber(next);
                  // Live warning so the agent sees the 10-or-16-digit rule as
                  // they type, not only on Search (issue #65).
                  setCardNumberError(cardNumberFormatError(next) ?? '');
                }}
                placeholder="Online or debit/credit card number"
                aria-invalid={!!cardNumberError}
                aria-describedby={
                  cardNumberError ? 'card-number-error' : undefined
                }
                className={inputClass}
              />
              {cardNumberError && (
                <p
                  id="card-number-error"
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
                >
                  {cardNumberError}
                </p>
              )}
            </div>
          )}

          {mode === 'account' && (
            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col gap-2 w-[200px]">
                <label htmlFor="nsc" className={labelClass}>
                  Sort code (NSC)
                </label>
                <input
                  id="nsc"
                  value={nsc}
                  onChange={(e) => setNsc(e.target.value.trim())}
                  placeholder="00-00-00"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2 w-[260px]">
                <label htmlFor="account-number" className={labelClass}>
                  Account number
                </label>
                <input
                  id="account-number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.trim())}
                  placeholder="Account number"
                  className={inputClass}
                />
              </div>
            </div>
          )}

          {(mode === 'name-dob' || mode === 'name-address') && (
            <div className="flex flex-col gap-4">
              <div className="flex gap-4 flex-wrap">
                <div className="flex flex-col gap-2 w-[260px]">
                  <label htmlFor="surname" className={labelClass}>
                    Surname
                  </label>
                  <input
                    id="surname"
                    value={surname}
                    // Issue #615 (@dylan219 2026-08-11): honour the gateway —
                    // accept A–Z letters only (accents decomposed, punctuation/
                    // spaces stripped) and cap at 6 letters. So "O'Brien" shows
                    // as "OBrien". `maxLength` is a native secondary guard for
                    // the same cap.
                    onChange={(e) =>
                      setSurname(
                        filterNameForDisplay(
                          e.target.value,
                          SURNAME_GATEWAY_LENGTH,
                        ),
                      )
                    }
                    placeholder="Surname (e.g. OBrien)"
                    maxLength={SURNAME_GATEWAY_LENGTH}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-2 w-[200px]">
                  <label htmlFor="first-name" className={labelClass}>
                    First name
                  </label>
                  <input
                    id="first-name"
                    value={firstName}
                    // Issue #615 (@dylan219 2026-08-11): honour the gateway —
                    // accept A–Z letters only (accents decomposed, punctuation/
                    // spaces stripped) and cap at 3 letters.
                    onChange={(e) =>
                      setFirstName(
                        filterNameForDisplay(
                          e.target.value,
                          FIRST_NAME_GATEWAY_LENGTH,
                        ),
                      )
                    }
                    placeholder="First name"
                    maxLength={FIRST_NAME_GATEWAY_LENGTH}
                    className={inputClass}
                  />
                </div>
              </div>
              {mode === 'name-dob' ? (
                <div className="flex flex-col gap-2 w-[260px]">
                  <label htmlFor="dob" className={labelClass}>
                    Date of birth
                  </label>
                  {/* Issue #102: accessible calendar pop-up with an easy YEAR
                      dropdown, while the field still accepts a typed
                      DD/MM/YYYY value (validated/converted by `toIsoDob`). */}
                  <DobPicker
                    id="dob"
                    value={dob}
                    onChange={setDob}
                    placeholder="dd/mm/yyyy"
                    inputClassName={`${inputClass} flex-1`}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-[400px]">
                  <label htmlFor="address" className={labelClass}>
                    First line of address
                  </label>
                  <input
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="First line of address"
                    className={inputClass}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={searching}
          aria-label="Search"
          className="px-6 py-3 bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] font-bold text-[16px] rounded-md hover:bg-[#e55a2a] transition-all focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {message && (
        <p
          role="status"
          className="mt-6 text-[16px] font-['Arial',sans-serif] text-[#1d252c]"
        >
          {message}
        </p>
      )}

      {/* Results list (Name / Address / DoB), CCF-44. */}
      {searched && results.length > 0 && (
        <section aria-label="Search results" className="mt-8 max-w-[1300px]">
          <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
            {total} {total === 1 ? 'customer' : 'customers'} found
            {truncated ? ' (showing the first 20)' : ''}
          </h2>
          <table
            className="results w-full border-collapse bg-white"
            role="table"
          >
            <thead>
              <tr>
                {[
                  'Title',
                  'Name',
                  'Address',
                  'Date of birth',
                  'Online card number',
                  'Accounts (sort code / account no.)',
                  'Card status',
                  '',
                ].map((h, i) => (
                  <th
                    key={h || `col-${i}`}
                    className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-[#dad7d3]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((hit) =>
                hit.OnlineCardsStatus === 'loaded' &&
                hit.OnlineCards &&
                hit.OnlineCards.length > 0 ? (
                  <MultiCardRows
                    key={hit.CustomerNumber}
                    hit={hit}
                    cards={hit.OnlineCards}
                    disabled={!sessionLoaded}
                    onSelectCard={(card) => onSelect(selectionFor(hit, card))}
                  />
                ) : (
                  <tr
                    key={hit.CustomerNumber}
                    className="border-b border-[#e5e7eb] hover:bg-[#f3f4f6]"
                    aria-label={`Customer ${hit.CustomerName}`}
                  >
                    <td className={cellClass}>{hit.CustomerTitle || '—'}</td>
                    <td className={cellClass}>{hit.CustomerName}</td>
                    <td className={cellClass}>{hit.Address || '—'}</td>
                    <td className={cellClass}>
                      {hit.Dob ? formatDob(hit.Dob) : '—'}
                    </td>
                    {isCardSelectionBlocked(hit) ? (
                      // #851 CR §5.4 + Q4: no online card reaches the searched
                      // account. Shown as a single row with an indicator;
                      // Select is disabled (fail closed).
                      <td colSpan={2} className={cellClass}>
                        <p
                          id={`online-cards-unavailable-${hit.CustomerNumber}`}
                          role="alert"
                          className="m-0 text-[#b91c1c]"
                        >
                          {NO_MATCHING_CARD_MESSAGE}
                        </p>
                      </td>
                    ) : hit.OnlineCardsStatus === 'unavailable' ? (
                      // #851 (@dylan219): a card lookup failed (rare) — show the
                      // customer as before this change: the single row under
                      // the online card the search returned, selectable. The
                      // BFF binds that same card, derived server-side from the
                      // search row.
                      <>
                        <td className={cellClass}>
                          <span className="whitespace-nowrap">
                            {hit.OnlineCardNumber}
                          </span>
                        </td>
                        <td className={cellClass}>—</td>
                      </>
                    ) : (
                      <>
                        <td className={cellClass}>—</td>
                        <td className={cellClass}>—</td>
                      </>
                    )}
                    <td className={cellClass}>
                      {blockedCardStatusLabel(hit) ?? cardStatusLabel(hit)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => onSelect(withFallbackSelection(hit))}
                        // Disabled until `/api/session` resolves, so the routing
                        // decision is never made against an unresolved session;
                        // and (#851 §5.4) when no online card reaches the
                        // searched account.
                        disabled={!sessionLoaded || isCardSelectionBlocked(hit)}
                        aria-label={`Select ${hit.CustomerName}`}
                        aria-describedby={
                          isCardSelectionBlocked(hit)
                            ? `online-cards-unavailable-${hit.CustomerNumber}`
                            : undefined
                        }
                        className={selectButtonClass}
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>

          {/* Next / Previous paging (CCF-44). */}
          <div className="mt-4 flex items-center gap-6">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              aria-label="Previous page"
              className="font-['Arial',sans-serif] text-[16px] underline px-3 py-2 rounded-md bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-[#005fcc] disabled:text-[#6b7280] disabled:cursor-not-allowed text-[#fc4c02] hover:text-[#b83205] disabled:hover:text-[#6b7280]"
            >
              &lt; Previous
            </button>
            <span className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]">
              Page {page + 1} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
              aria-label="Next page"
              className="font-['Arial',sans-serif] text-[16px] underline px-3 py-2 rounded-md bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-[#005fcc] disabled:text-[#6b7280] disabled:cursor-not-allowed text-[#fc4c02] hover:text-[#b83205] disabled:hover:text-[#6b7280]"
            >
              Next &gt;
            </button>
          </div>
        </section>
      )}

      {/* CCF-44 "Error 2" pop-up: the selected customer HAS an Open24 number on
          Unibanks, so they must go through standard ID&V or re-register — the
          dummy-number mechanism does NOT apply. Directs the agent to ID&V. */}
      {error2For && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="error2-heading"
        >
          <div className="w-full max-w-[520px] bg-white border-2 border-[#dc2626] rounded-md shadow-xl p-8">
            <h2
              id="error2-heading"
              className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3"
            >
              Error 2 — Open24 number on file
            </h2>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
              This customer has an Open24 number on Unibanks. They must be taken
              through standard ID&amp;V or re-registered before you can proceed.
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setError2For(null);
                  navigate('/idv');
                }}
                aria-label="Go to ID&V"
                className="px-6 py-3 bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] font-bold text-[16px] rounded-md hover:bg-[#e55a2a] transition-all focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2"
              >
                Go to ID&amp;V
              </button>
              <button
                type="button"
                onClick={() => setError2For(null)}
                aria-label="Close"
                className="px-6 py-3 bg-white text-[#1d252c] border-2 border-[#1d252c] font-['Arial',sans-serif] font-bold text-[16px] rounded-md hover:bg-[#f3f4f6] transition-all focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #72 (@dylan219, 2026-08-21) — verification-required warning: a
          NON-privileged role selected a REGISTERED customer while on an ONLINE
          interaction. Searching + skipping ID&V is reserved for the four
          privileged roles; this role must verify the customer first. "Go to
          ID&V" seeds the online card number returned in the search into context
          and navigates to ID&V, where the card-lookup auto-runs and the agent is
          presented with the normal PAN option. */}
      {idvRequiredFor && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="idv-required-heading"
        >
          <div className="w-full max-w-[520px] bg-white border-2 border-[#dc2626] rounded-md shadow-xl p-8">
            <h2
              id="idv-required-heading"
              className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3"
            >
              Identity &amp; Verification required
            </h2>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
              This is a registered customer. Before you can proceed you must
              take them through Identity &amp; Verification (ID&amp;V). Select
              &ldquo;Go to ID&amp;V&rdquo; to verify the customer using their
              online card number.
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  const hit = idvRequiredFor;
                  setIdvRequiredFor(null);
                  // Seed the online card number returned in the search so the
                  // ID&V screen auto-runs the card-number lookup (→ PAN option),
                  // exactly as the normal ID&V process. The search returns the
                  // 16-digit OnlineCardNumber; reduce it to the 10-digit Open24
                  // number the ID&V card field expects (onlineCardDisplay).
                  //
                  // CRITICAL: `onSelect` already pushed this customer into
                  // context via `setCustomerContext(hit)` — so `customer.cif` is
                  // now populated. The ID&V seed effect (IdvScreen) bails out
                  // when a CIF is already present (`if (customer.cif) return`),
                  // so a bare `updateCustomer({ pendingIdvCardNumber })` (which
                  // merges ON TOP of the stale CIF) would SUPPRESS the seeded
                  // card lookup — the agent would land on ID&V with no auto-run
                  // PAN challenge and no server-bound identification (the whole
                  // point of "Go to ID&V"). Reset the client-side identity to a
                  // blank slate first (preserving only the live-online-journey
                  // anchors so we don't drop the call to Offline), then set ONLY
                  // the pending card. This makes the handoff unambiguous: no
                  // stale CIF, so the seed effect fires the card lookup and ID&V
                  // re-establishes the server-side identification from scratch.
                  setCustomer({
                    ...resetIdentityPreservingOnlineJourney(customer),
                    pendingIdvCardNumber: onlineCardDisplay(
                      hit.OnlineCardNumber,
                    ),
                  });
                  navigate('/idv');
                }}
                aria-label="Go to ID&V"
                className="px-6 py-3 bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] font-bold text-[16px] rounded-md hover:bg-[#e55a2a] transition-all focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2"
              >
                Go to ID&amp;V
              </button>
              <button
                type="button"
                onClick={() => setIdvRequiredFor(null)}
                aria-label="Close"
                className="px-6 py-3 bg-white text-[#1d252c] border-2 border-[#1d252c] font-['Arial',sans-serif] font-bold text-[16px] rounded-md hover:bg-[#f3f4f6] transition-all focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discreet Ardowen branding, bottom-right (issue #41). */}
      <ArdowenLogo />
    </main>
  );
}
